import React, { useState, useEffect, useRef } from 'react';
import { useEditor, sizeForPreset, orientationOf, presetKeyForPage } from '../../context/EditorContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { ScrollArea } from '../ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Switch } from '../ui/switch';
import { Separator } from '../ui/separator';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Settings2,
  Layers,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Trash2,
  ChevronUp,
  ChevronDown,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Superscript,
  Subscript,
  RotateCw,
  Palette,
  Image,
  GripVertical,
  RectangleVertical,
  RectangleHorizontal,
} from 'lucide-react';
import { toast } from 'sonner';
import { FontPicker } from './FontPicker';
import { GradientEditor } from './GradientEditor';
import { gradientFromColor, gradientToCss, describeGradient } from '../../lib/gradients';
import { strokeHasGradient, strokeHasShimmer, solidStroke } from '../../lib/fills';
import { fillToCss } from '../../lib/colors';
import { ptToPx, fmtPt, isExactLineHeight, exactLineHeightPx, exactLineHeight, lineHeightMultiple, PT_PER_PX, CANVAS_UNITS } from '../../lib/units';
import { useTextCommands } from '../../hooks/useTextCommands';

export const RightSidebar = () => {
  const { state, dispatch, getCurrentPage, getSelectedObjects, getActiveObjects, pxToUnit } = useEditor();
  const currentPage = getCurrentPage();
  const selectedObjects = getSelectedObjects();
  const selectedObject = selectedObjects.length === 1 ? selectedObjects[0] : null;
  const activeObjects = getActiveObjects();
  
  return (
    <aside 
      className="w-72 xl:w-80 h-full shrink-0 border-l border-border bg-card flex flex-col z-30"
      data-testid="right-sidebar"
    >
      <Tabs 
        value={state.rightPanelTab} 
        onValueChange={(v) => dispatch({ type: 'SET_RIGHT_PANEL_TAB', payload: v })}
        className="flex-1 flex flex-col"
      >
        <TabsList className="w-full bg-muted/50 rounded-none border-b border-border h-10 p-0">
          <TabsTrigger 
            value="properties" 
            className="flex-1 rounded-none data-[state=active]:bg-background data-[state=active]:text-foreground text-muted-foreground h-full"
          >
            <Settings2 className="w-4 h-4 mr-2" /> Properties
          </TabsTrigger>
          <TabsTrigger 
            value="layers" 
            className="flex-1 rounded-none data-[state=active]:bg-background data-[state=active]:text-foreground text-muted-foreground h-full"
          >
            <Layers className="w-4 h-4 mr-2" /> Layers
          </TabsTrigger>
        </TabsList>
        
        {/* Properties Tab */}
        <TabsContent value="properties" className="flex-1 m-0 overflow-hidden">
          <ScrollArea className="h-[calc(100vh-160px)]">
            {selectedObject ? (
              <ObjectProperties object={selectedObject} dispatch={dispatch} pxToUnit={pxToUnit} />
            ) : selectedObjects.length > 1 ? (
              <MultiSelectProperties objects={selectedObjects} dispatch={dispatch} />
            ) : (
              <PageProperties page={currentPage} pageIndex={state.currentPageIndex} dispatch={dispatch} pxToUnit={pxToUnit} />
            )}
          </ScrollArea>
        </TabsContent>
        
        {/* Layers Tab */}
        <TabsContent value="layers" className="flex-1 m-0 overflow-hidden">
          <LayersPanel 
            objects={activeObjects} 
            selectedIds={state.selectedObjectIds}
            dispatch={dispatch}
            scratchIds={state.viewMode === 'master' ? [] : (state.scratch?.objects || []).map((o) => o.id)}
          />
        </TabsContent>
      </Tabs>
    </aside>
  );
};

const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

/**
 * Position / size field in the canvas unit (View ▸ Units: in, cm, pt, px — Publisher's measurement unit). Shows the
 * object's px value converted; commits the px value on blur / Enter (so typing "2.5" is not applied digit by digit);
 * arrow keys nudge by one ruler step (Shift: ten).
 */
const UnitPosInput = ({ px, unit, pxToUnit, unitToPx, onCommit, min = -99999, label, testId }) => {
  const digits = unit === 'px' ? 0 : unit === 'pt' ? 1 : 2;
  const step = unit === 'px' ? 1 : unit === 'pt' ? 1 : unit === 'cm' ? 0.1 : 0.05;
  const fmt = (v) => Number(pxToUnit(v, unit, digits)).toFixed(digits);
  const [text, setText] = useState(fmt(px));
  const [editing, setEditing] = useState(false);
  useEffect(() => { if (!editing) setText(fmt(px)); }, [px, unit, editing]); // eslint-disable-line react-hooks/exhaustive-deps
  const commit = () => {
    setEditing(false);
    const v = parseFloat(String(text).replace(',', '.'));
    if (!Number.isFinite(v)) { setText(fmt(px)); return; }
    const next = Math.max(min, Math.round(unitToPx(v, unit) * 100) / 100);
    if (Math.abs(next - px) > 0.001) onCommit(next); else setText(fmt(px));
  };
  const nudge = (dir, big) => { const v = (parseFloat(text) || Number(pxToUnit(px, unit, digits))) + dir * step * (big ? 10 : 1); onCommit(Math.max(min, Math.round(unitToPx(v, unit) * 100) / 100)); };
  return (
    <div className="relative">
      <Input
        type="text"
        inputMode="decimal"
        value={text}
        aria-label={`${label} (${unit})`}
        onFocus={() => setEditing(true)}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.currentTarget.blur(); } else if (e.key === 'ArrowUp') { e.preventDefault(); nudge(1, e.shiftKey); } else if (e.key === 'ArrowDown') { e.preventDefault(); nudge(-1, e.shiftKey); } }}
        className="h-8 bg-background border-border pr-7"
        data-testid={testId}
        data-px={px}
      />
      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground pointer-events-none">{unit}</span>
    </div>
  );
};

const ObjectProperties = ({ object, dispatch, pxToUnit }) => {
  const { openDialog, state, unitToPx } = useEditor();
  const unit = state.canvasUnit || 'in';
  const [rotationLocal, setRotationLocal] = useState(r2(object.rotation || 0));
  useEffect(() => { setRotationLocal(r2(object.rotation || 0)); }, [object]);
  
  const updateObject = (data) => {
    dispatch({ type: 'UPDATE_OBJECT', payload: { id: object.id, data } });
  };
  
  const handleInputChange = (field, value) => {
    const numValue = parseFloat(value) || 0;
    if (field === 'rotation') setRotationLocal(numValue);
    updateObject({ [field]: numValue });
  };
  
  return (
    <div className="p-4 space-y-4">
      <Accordion type="multiple" defaultValue={['transform', 'appearance', 'text']} className="space-y-2">
        {/* Transform — position and size in the canvas unit (View ▸ Units) */}
        <AccordionItem value="transform" className="border border-border rounded-lg overflow-hidden">
          <AccordionTrigger className="px-3 py-2 hover:no-underline hover:bg-accent/50 text-foreground">
            <span className="text-sm font-medium">Transform <span className="text-xs text-muted-foreground font-normal" data-testid="props-transform-unit">({unit})</span></span>
          </AccordionTrigger>
          <AccordionContent className="px-3 pb-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-foreground/70">X</Label>
                <UnitPosInput px={object.x} unit={unit} pxToUnit={pxToUnit} unitToPx={unitToPx} label="X" onCommit={(x) => updateObject({ x })} testId="props-x" />
              </div>
              <div>
                <Label className="text-xs text-foreground/70">Y</Label>
                <UnitPosInput px={object.y} unit={unit} pxToUnit={pxToUnit} unitToPx={unitToPx} label="Y" onCommit={(y) => updateObject({ y })} testId="props-y" />
              </div>
              <div>
                <Label className="text-xs text-foreground/70">Width</Label>
                <UnitPosInput px={object.width} unit={unit} pxToUnit={pxToUnit} unitToPx={unitToPx} label="Width" min={1} onCommit={(width) => updateObject({ width })} testId="props-width" />
              </div>
              <div>
                <Label className="text-xs text-foreground/70">Height</Label>
                <UnitPosInput px={object.height} unit={unit} pxToUnit={pxToUnit} unitToPx={unitToPx} label="Height" min={1} onCommit={(height) => updateObject({ height })} testId="props-height" />
              </div>
            </div>
            <div className="mt-3">
              <Label className="text-xs text-foreground/70">Rotation</Label>
              <div className="flex items-center gap-2">
                <Slider
                  value={[rotationLocal]}
                  onValueChange={([v]) => handleInputChange('rotation', v)}
                  min={0}
                  max={360}
                  step={1}
                  className="flex-1"
                />
                <Input
                  type="number"
                  value={rotationLocal}
                  onChange={(e) => handleInputChange('rotation', e.target.value)}
                  className="w-16 h-8 bg-background border-border"
                  data-testid="props-rotation"
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
        
        {/* Appearance */}
        <AccordionItem value="appearance" className="border border-border rounded-lg overflow-hidden">
          <AccordionTrigger className="px-3 py-2 hover:no-underline hover:bg-accent/50 text-foreground">
            <span className="text-sm font-medium">Appearance</span>
          </AccordionTrigger>
          <AccordionContent className="px-3 pb-3">
            {object.type === 'shape' && (
              <>
                {/* Fill Type Selector */}
                <div className="mb-3">
                  <Label className="text-xs text-foreground/70">Fill Type</Label>
                  <Select 
                    value={object.properties?.fill?.type || 'solid'} 
                    onValueChange={(v) => updateObject({ 
                      properties: { 
                        ...object.properties, 
                        fill: { 
                          ...object.properties?.fill,
                          type: v,
                          color: object.properties?.fill?.color || '#7c3aed',
                          gradient: object.properties?.fill?.gradient || gradientFromColor(object.properties?.fill?.color || '#7c3aed'),
                          image: object.properties?.fill?.image || null
                        } 
                      } 
                    })}
                  >
                    <SelectTrigger className="h-8 mt-1 bg-background border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      <SelectItem value="solid">Solid Color</SelectItem>
                      <SelectItem value="gradient">Gradient</SelectItem>
                      <SelectItem value="image">Image</SelectItem>
                      <SelectItem value="none">No Fill</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Solid Color Fill */}
                {(object.properties?.fill?.type === 'solid' || !object.properties?.fill?.type) && (
                  <div className="mb-3">
                    <Label className="text-xs text-foreground/70">Fill Color</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={object.properties?.fill?.color || '#7c3aed'}
                        onChange={(e) => updateObject({ 
                          properties: { 
                            ...object.properties, 
                            fill: { type: 'solid', color: e.target.value } 
                          } 
                        })}
                        className="w-8 h-8 rounded cursor-pointer"
                      />
                      <Input
                        value={object.properties?.fill?.color || '#7c3aed'}
                        onChange={(e) => updateObject({ 
                          properties: { 
                            ...object.properties, 
                            fill: { type: 'solid', color: e.target.value } 
                          } 
                        })}
                        className="flex-1 h-8 bg-background border-border"
                      />
                    </div>
                  </div>
                )}
                
                {/* Gradient Fill — the shared gradient creator (compact); "More…" opens the full Fill Effects dialog */}
                {object.properties?.fill?.type === 'gradient' && (
                  <div className="mb-3">
                    <GradientEditor
                      compact
                      value={object.properties?.fill?.gradient}
                      onChange={(gradient) => updateObject({ properties: { ...object.properties, fill: { ...object.properties?.fill, type: 'gradient', gradient } } })}
                      onMore={() => openDialog('fill-effects', { target: 'fill', tab: 'gradient' })}
                      testId="pane-gradient"
                    />
                  </div>
                )}
                
                {/* Image Fill */}
                {object.properties?.fill?.type === 'image' && (
                  <div className="mb-3 space-y-3">
                    {/* Image URL or Upload */}
                    <div>
                      <Label className="text-xs text-foreground/70">Image Source</Label>
                      <div className="space-y-2 mt-1">
                        <Input
                          placeholder="Paste image URL..."
                          value={object.properties?.fill?.image?.url || ''}
                          onChange={(e) => updateObject({ 
                            properties: { 
                              ...object.properties, 
                              fill: { 
                                ...object.properties?.fill,
                                type: 'image',
                                image: { ...object.properties?.fill?.image, url: e.target.value }
                              } 
                            } 
                          })}
                          className="h-8 bg-background border-border"
                        />
                        <div className="text-center text-xs text-muted-foreground">or</div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full border-border"
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/*';
                            input.onchange = (e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onload = (ev) => {
                                  updateObject({ 
                                    properties: { 
                                      ...object.properties, 
                                      fill: { 
                                        ...object.properties?.fill,
                                        type: 'image',
                                        image: { 
                                          ...object.properties?.fill?.image, 
                                          url: ev.target.result,
                                          name: file.name
                                        }
                                      } 
                                    } 
                                  });
                                };
                                reader.readAsDataURL(file);
                              }
                            };
                            input.click();
                          }}
                        >
                          <Image className="w-4 h-4 mr-2" />
                          Upload Image
                        </Button>
                      </div>
                    </div>
                    
                    {/* Image Fit */}
                    {object.properties?.fill?.image?.url && (
                      <>
                        <div>
                          <Label className="text-xs text-foreground/70">Image Fit</Label>
                          <Select 
                            value={object.properties?.fill?.image?.fit || 'cover'} 
                            onValueChange={(v) => updateObject({ 
                              properties: { 
                                ...object.properties, 
                                fill: { 
                                  ...object.properties?.fill,
                                  image: { ...object.properties?.fill?.image, fit: v }
                                } 
                              } 
                            })}
                          >
                            <SelectTrigger className="h-8 mt-1 bg-background border-border">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-popover border-border">
                              <SelectItem value="cover">Cover (fill shape)</SelectItem>
                              <SelectItem value="contain">Contain (fit inside)</SelectItem>
                              <SelectItem value="stretch">Stretch (distort)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {/* Image Position */}
                        <div>
                          <Label className="text-xs text-foreground/70">Image Position</Label>
                          <Select 
                            value={object.properties?.fill?.image?.position || 'center'} 
                            onValueChange={(v) => updateObject({ 
                              properties: { 
                                ...object.properties, 
                                fill: { 
                                  ...object.properties?.fill,
                                  image: { ...object.properties?.fill?.image, position: v }
                                } 
                              } 
                            })}
                          >
                            <SelectTrigger className="h-8 mt-1 bg-background border-border">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-popover border-border">
                              <SelectItem value="center">Center</SelectItem>
                              <SelectItem value="top">Top</SelectItem>
                              <SelectItem value="bottom">Bottom</SelectItem>
                              <SelectItem value="left">Left</SelectItem>
                              <SelectItem value="right">Right</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {/* Image Preview */}
                        <div 
                          className="h-16 rounded border border-border overflow-hidden"
                          style={{
                            backgroundImage: `url(${object.properties?.fill?.image?.url})`,
                            backgroundSize: object.properties?.fill?.image?.fit || 'cover',
                            backgroundPosition: object.properties?.fill?.image?.position || 'center',
                            backgroundRepeat: 'no-repeat',
                          }}
                        />
                        
                        {/* Remove Image Button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full text-destructive hover:text-destructive"
                          onClick={() => updateObject({ 
                            properties: { 
                              ...object.properties, 
                              fill: { type: 'solid', color: '#7c3aed' }
                            } 
                          })}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Remove Image
                        </Button>
                      </>
                    )}
                  </div>
                )}
                
                <div className="mb-3">
                  <Label className="text-xs text-foreground/70">Stroke Width</Label>
                  <Slider
                    value={[object.properties?.stroke?.width || 0]}
                    onValueChange={([v]) => updateObject({ 
                      properties: { 
                        ...object.properties, 
                        stroke: { ...object.properties?.stroke, width: v } 
                      } 
                    })}
                    min={0}
                    max={20}
                    step={1}
                    className="mt-2"
                  />
                </div>
                {(object.properties?.stroke?.width > 0) && (
                  <div>
                    <Label className="text-xs text-foreground/70">Stroke Color</Label>
                    {strokeHasGradient(object.properties?.stroke) ? (
                      <div className="flex items-center gap-2 mt-1" data-testid="pane-stroke-gradient">
                        <span className="h-8 w-8 shrink-0 rounded border border-border wp-checker overflow-hidden"><span className="block h-full w-full" style={{ background: gradientToCss(object.properties.stroke.gradient) }} /></span>
                        <span className="flex-1 min-w-0 text-xs text-foreground truncate" data-testid="pane-stroke-gradient-summary">{describeGradient(object.properties.stroke.gradient)}{strokeHasShimmer(object.properties.stroke) ? ' · shimmer' : ''}</span>
                        <Button type="button" variant="outline" size="sm" className="h-8 px-2 text-xs border-border shrink-0" onClick={() => updateObject({ properties: { ...object.properties, stroke: solidStroke(object.properties.stroke, object.properties.stroke.color) } })} title="Back to a solid colour outline" data-testid="pane-stroke-gradient-solid">Solid</Button>
                      </div>
                    ) : (
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={object.properties?.stroke?.color || '#000000'}
                        onChange={(e) => updateObject({ 
                          properties: { 
                            ...object.properties, 
                            stroke: { ...object.properties?.stroke, color: e.target.value } 
                          } 
                        })}
                        className="w-8 h-8 rounded cursor-pointer"
                        aria-label="Stroke colour"
                      />
                      <Input
                        value={object.properties?.stroke?.color || '#000000'}
                        onChange={(e) => updateObject({ 
                          properties: { 
                            ...object.properties, 
                            stroke: { ...object.properties?.stroke, color: e.target.value } 
                          } 
                        })}
                        className="flex-1 h-8 bg-background border-border"
                        aria-label="Stroke colour (hex)"
                      />
                    </div>
                    )}
                    <Button type="button" variant="outline" size="sm" className="w-full mt-2 h-8 text-xs border-border" onClick={() => openDialog('fill-effects', { target: 'stroke', tab: 'gradient' })} data-testid="pane-stroke-gradient-open">
                      <span className="h-3.5 w-3.5 rounded-sm border border-border mr-2 wp-checker overflow-hidden"><span className="block h-full w-full" style={{ background: strokeHasGradient(object.properties?.stroke) ? gradientToCss(object.properties.stroke.gradient) : 'linear-gradient(135deg, #7c3aed, #c4b5fd)' }} /></span>
                      {strokeHasGradient(object.properties?.stroke) ? 'Edit gradient outline…' : 'Gradient outline…'}
                    </Button>
                  </div>
                )}
                <div className="mt-3">
                  <Label className="text-xs text-foreground/70">Corner Radius</Label>
                  <Slider
                    value={[object.properties?.cornerRadius || 0]}
                    onValueChange={([v]) => updateObject({ 
                      properties: { ...object.properties, cornerRadius: v } 
                    })}
                    min={0}
                    max={50}
                    step={1}
                    className="mt-2"
                  />
                </div>
                <div className="mt-3">
                  <Label className="text-xs text-foreground/70">Opacity</Label>
                  <Slider
                    value={[object.properties?.opacity ?? 100]}
                    onValueChange={([v]) => updateObject({ 
                      properties: { ...object.properties, opacity: v } 
                    })}
                    min={0}
                    max={100}
                    step={1}
                    className="mt-2"
                  />
                </div>
              </>
            )}
            
            {object.type === 'image' && (
              <>
                <div className="mb-3">
                  <Label className="text-xs text-foreground/70">Opacity</Label>
                  <Slider
                    value={[object.properties?.opacity ?? 100]}
                    onValueChange={([v]) => updateObject({ 
                      properties: { ...object.properties, opacity: v } 
                    })}
                    min={0}
                    max={100}
                    step={1}
                    className="mt-2"
                  />
                </div>
                <div className="mt-3">
                  <Label className="text-xs text-foreground/70">Border Radius</Label>
                  <Slider
                    value={[object.properties?.borderRadius || 0]}
                    onValueChange={([v]) => updateObject({ 
                      properties: { ...object.properties, borderRadius: v } 
                    })}
                    min={0}
                    max={50}
                    step={1}
                    className="mt-2"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full mt-3 border-border hover:bg-accent"
                  onClick={() => dispatch({ type: 'SHOW_PHOTO_EDITOR', payload: object })}
                >
                  <Image className="w-4 h-4 mr-2" /> Edit Image
                </Button>
              </>
            )}
          </AccordionContent>
        </AccordionItem>
        
        {/* Text Properties */}
        {(object.type === 'text' || object.type === 'textbox') && (
          <AccordionItem value="text" className="border border-border rounded-lg overflow-hidden">
            <AccordionTrigger className="px-3 py-2 hover:no-underline hover:bg-accent/50 text-foreground">
              <span className="text-sm font-medium">Text</span>
            </AccordionTrigger>
            <AccordionContent className="px-3 pb-3">
              <TextProperties object={object} dispatch={dispatch} />
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>
      
      {/* Quick Actions */}
      <div className="pt-2 border-t border-border">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => dispatch({ type: 'UPDATE_OBJECT', payload: { id: object.id, data: { locked: !object.locked } } })}
          >
            {object.locked ? <Lock className="w-4 h-4 mr-1" /> : <Unlock className="w-4 h-4 mr-1" />}
            {object.locked ? 'Locked' : 'Lock'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={() => dispatch({ type: 'DELETE_OBJECTS', payload: [object.id] })}
          >
            <Trash2 className="w-4 h-4 mr-1" /> Delete
          </Button>
        </div>
      </div>
    </div>
  );
};

const TextProperties = ({ object, dispatch }) => {
  const { state } = useEditor();
  const props = object.properties || {};
  // Same command set as the ribbon: highlighted text / the caret's word while editing, the whole box otherwise
  // (see useTextCommands) — so a box can mix fonts, sizes and colours from here too.
  const t = useTextCommands();
  const editingThis = t.editing && state.editingObjectId === object.id;
  const keepFocus = (e) => { if (editingThis && e.button === 0) e.preventDefault(); };

  const updateText = (data) => {
    dispatch({
      type: 'UPDATE_OBJECT',
      payload: { id: object.id, data: { properties: { ...props, ...data } } }
    });
  };

  // Font size commits on Enter / blur (typing "48" must not apply "4" first)
  // (applying to edited text re-focuses the box, which blurs the input inside the first commit → guard the nested one)
  const committing = useRef(false);
  const once = (fn) => { if (committing.current) return; committing.current = true; try { fn(); } finally { committing.current = false; } };
  // the fields talk points (Publisher); sizes are stored in page px
  const [sizeLocal, setSizeLocal] = useState(fmtPt(t.currentFontSize || 16));
  useEffect(() => { setSizeLocal(fmtPt(t.currentFontSize || 16)); }, [t.currentFontSize]);
  const commitSize = () => once(() => { const n = parseFloat(sizeLocal); if (Number.isFinite(n) && n > 0) t.setFontSize(ptToPx(n)); else setSizeLocal(fmtPt(t.currentFontSize || 16)); });
  // Line spacing: a paragraph property — the highlighted paragraphs while editing, the whole box otherwise (commits like the
  // size). A multiple edits as a number; an exact height (Paragraph dialog ▸ Exactly) edits in points.
  const lineExact = isExactLineHeight(t.currentLineHeight);
  const lineShown = () => (lineExact ? fmtPt(exactLineHeightPx(t.currentLineHeight)) : String(Math.round(lineHeightMultiple(t.currentLineHeight) * 100) / 100));
  const [lineLocal, setLineLocal] = useState(lineShown);
  useEffect(() => { setLineLocal(lineShown()); }, [t.currentLineHeight]); // eslint-disable-line react-hooks/exhaustive-deps
  const commitLine = () => once(() => {
    const n = parseFloat(lineLocal);
    if (lineExact ? Number.isFinite(n) && n >= 1 && n <= 400 : Number.isFinite(n) && n >= 0.5 && n <= 6) t.setLineSpacing(lineExact ? exactLineHeight(n) : n);
    else setLineLocal(lineShown());
  });

  const currentColor = editingThis ? t.currentColor : (props.color || '#000000'); // same reading as the ribbon swatch
  const [colorLocal, setColorLocal] = useState(currentColor);
  useEffect(() => { setColorLocal(currentColor); }, [currentColor]);
  const commitColor = (v) => once(() => { if (/^#[0-9a-f]{6}$/i.test(v)) t.setColor(v); else setColorLocal(currentColor); });

  return (
    <div className="space-y-3" data-keep-editing data-testid="props-text-section">
      {editingThis && <p className="text-[11px] text-muted-foreground" data-testid="props-text-scope-hint">{t.hasSelection ? 'Formatting applies to the highlighted text.' : 'Formatting applies to the word at the cursor, or to what you type next.'}</p>}
      <FontPicker
        value={t.currentFontFamily || 'Inter'}
        onChange={t.setFontFamily}
        onPreview={t.previewFontFamily}
        testId="props-font-family"
      />

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs text-foreground/70">Size (pt)</Label>
          <Input
            type="number"
            step="0.5"
            value={sizeLocal}
            onChange={(e) => setSizeLocal(e.target.value)}
            onBlur={commitSize}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitSize(); e.currentTarget.blur(); } }}
            className="h-8 bg-background border-border"
            data-testid="props-font-size"
          />
        </div>
        <div>
          <Label className="text-xs text-foreground/70" title={editingThis ? 'Publisher: line spacing is a paragraph setting — highlighted paragraphs change; with nothing highlighted the whole text box does' : undefined}>{editingThis ? (t.hasSelection ? 'Line Spacing (highlighted paragraphs, ' : 'Line Spacing (whole box, ') : 'Line Spacing ('}{lineExact ? <span data-testid="props-line-spacing-exact" title="Exact line spacing in points (Paragraph dialog ▸ Between lines)">pt</span> : 'sp'})</Label>
          <Input
            type="number"
            step={lineExact ? '1' : '0.1'}
            min={lineExact ? '1' : '0.5'}
            max={lineExact ? '400' : '6'}
            title={lineExact ? 'Exact line spacing in points — Paragraph dialog ▸ Between lines switches back to multiples' : undefined}
            value={lineLocal}
            onChange={(e) => setLineLocal(e.target.value)}
            onBlur={commitLine}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitLine(); e.currentTarget.blur(); } }}
            className="h-8 bg-background border-border"
            data-testid="props-line-height"
          />
        </div>
      </div>

      {/* Text Style Buttons — inline while editing (selection / word / typing style), whole box otherwise */}
      <div className="flex items-center gap-1" data-testid="props-text-style">
        {[['bold', Bold, 'Bold'], ['italic', Italic, 'Italic'], ['underline', Underline, 'Underline'], ['strikeThrough', Strikethrough, 'Strikethrough']].map(([cmd, Icon, label]) => (
          <Button key={cmd} variant={t.isActive(cmd) ? 'default' : 'ghost'} size="icon" className="w-8 h-8" title={label} aria-label={label} aria-pressed={t.isActive(cmd)} onMouseDown={keepFocus} onClick={() => t.toggle(cmd)} data-testid={`props-text-${cmd.toLowerCase()}`}>
            <Icon className="w-4 h-4" />
          </Button>
        ))}
        <Separator orientation="vertical" className="h-6 mx-1" />
        {[['superscript', Superscript, 'Superscript'], ['subscript', Subscript, 'Subscript']].map(([cmd, Icon, label]) => (
          <Button key={cmd} variant={t.isActive(cmd) ? 'default' : 'ghost'} size="icon" className="w-8 h-8" title={label} aria-label={label} aria-pressed={t.isActive(cmd)} onMouseDown={keepFocus} onClick={() => t.toggle(cmd)} data-testid={`props-text-${cmd}`}>
            <Icon className="w-4 h-4" />
          </Button>
        ))}
      </div>

      {/* Text Alignment — a paragraph property: while the box is being edited it applies to the caret paragraph */}
      <div className="flex items-center gap-1" data-testid="props-text-align">
        {[['left', AlignLeft, 'Align left'], ['center', AlignCenter, 'Center'], ['right', AlignRight, 'Align right'], ['justify', AlignJustify, 'Justify']].map(([a, Icon, label]) => (
          <Button
            key={a}
            variant={t.alignActive(a) ? 'default' : 'ghost'}
            size="icon"
            className="w-8 h-8"
            title={editingThis ? `${label} this paragraph` : label}
            aria-label={label}
            onMouseDown={keepFocus}
            onClick={() => t.setAlign(a)}
            data-testid={`props-text-align-${a}`}
          >
            <Icon className="w-4 h-4" />
          </Button>
        ))}
      </div>

      {/* Text Color */}
      <div>
        <Label className="text-xs text-foreground/70">Text Color</Label>
        <div className="flex items-center gap-2 mt-1">
          <input
            type="color"
            value={/^#[0-9a-f]{6}$/i.test(colorLocal) ? colorLocal : '#000000'}
            onChange={(e) => { setColorLocal(e.target.value); t.setColor(e.target.value); }}
            className="w-8 h-8 rounded cursor-pointer"
            aria-label="Text colour"
            data-testid="props-text-color-swatch"
          />
          <Input
            value={colorLocal}
            onChange={(e) => setColorLocal(e.target.value)}
            onBlur={() => commitColor(colorLocal)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitColor(colorLocal); e.currentTarget.blur(); } }}
            className="flex-1 h-8 bg-background border-border"
            aria-label="Text colour (hex)"
            data-testid="props-text-color-hex"
          />
        </div>
      </div>

      {/* Columns */}
      <div>
        <Label className="text-xs text-foreground/70">Columns</Label>
        <div className="flex items-center gap-2 mt-1">
          <Select 
            value={String(props.columns || 1)} 
            onValueChange={(v) => updateText({ columns: parseInt(v) })}
          >
            <SelectTrigger className="h-8 bg-background border-border">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border">
              <SelectItem value="1">1 Column</SelectItem>
              <SelectItem value="2">2 Columns</SelectItem>
              <SelectItem value="3">3 Columns</SelectItem>
              <SelectItem value="4">4 Columns</SelectItem>
            </SelectContent>
          </Select>
          {(props.columns || 1) > 1 && (
            <div className="flex items-center gap-1">
              <Label className="text-xs text-foreground/70">Gap</Label>
              <Input
                type="number"
                value={props.columnGap || 16}
                onChange={(e) => updateText({ columnGap: parseInt(e.target.value) || 16 })}
                className="w-16 h-8 bg-background border-border"
              />
            </div>
          )}
        </div>
      </div>
      
      {/* Margins / Padding */}
      <div>
        <Label className="text-xs text-foreground/70">Inner Margins (px)</Label>
        <div className="grid grid-cols-4 gap-1 mt-1">
          <div className="text-center">
            <span className="text-xs text-muted-foreground">Top</span>
            <Input
              type="number"
              value={props.paddingTop ?? props.padding ?? 8}
              onChange={(e) => updateText({ paddingTop: parseInt(e.target.value) || 0 })}
              className="h-8 bg-background border-border text-center"
              data-testid="margin-top"
            />
          </div>
          <div className="text-center">
            <span className="text-xs text-muted-foreground">Right</span>
            <Input
              type="number"
              value={props.paddingRight ?? props.padding ?? 8}
              onChange={(e) => updateText({ paddingRight: parseInt(e.target.value) || 0 })}
              className="h-8 bg-background border-border text-center"
              data-testid="margin-right"
            />
          </div>
          <div className="text-center">
            <span className="text-xs text-muted-foreground">Bottom</span>
            <Input
              type="number"
              value={props.paddingBottom ?? props.padding ?? 8}
              onChange={(e) => updateText({ paddingBottom: parseInt(e.target.value) || 0 })}
              className="h-8 bg-background border-border text-center"
              data-testid="margin-bottom"
            />
          </div>
          <div className="text-center">
            <span className="text-xs text-muted-foreground">Left</span>
            <Input
              type="number"
              value={props.paddingLeft ?? props.padding ?? 8}
              onChange={(e) => updateText({ paddingLeft: parseInt(e.target.value) || 0 })}
              className="h-8 bg-background border-border text-center"
              data-testid="margin-left"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const MultiSelectProperties = ({ objects, dispatch }) => {
  return (
    <div className="p-4">
      <div className="text-center text-muted-foreground py-8">
        <p className="text-sm">{objects.length} objects selected</p>
        <div className="flex justify-center gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            className="border-border"
            onClick={() => dispatch({ type: 'GROUP_OBJECTS' })}
          >
            Group
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-border text-destructive hover:text-destructive"
            onClick={() => dispatch({ type: 'DELETE_OBJECTS', payload: objects.map(o => o.id) })}
          >
            Delete All
          </Button>
        </div>
      </div>
    </div>
  );
};

/** Page-size units for the Properties panel (96 px per inch). */
const SIZE_UNITS = [
  { id: 'in', label: 'in', perPx: 1 / 96, digits: 2, step: 0.25 },
  { id: 'cm', label: 'cm', perPx: 2.54 / 96, digits: 2, step: 0.5 },
  { id: 'mm', label: 'mm', perPx: 25.4 / 96, digits: 1, step: 5 },
  { id: 'pt', label: 'pt', perPx: PT_PER_PX, digits: 1, step: 6 },
  { id: 'px', label: 'px', perPx: 1, digits: 0, step: 1 },
];
const MIN_PAGE_PX = 48; // ½ in
const MAX_PAGE_PX = 96 * 200; // 200 in (banners)

/** Width / height field that shows and accepts a value in the chosen unit and commits the px value on blur / Enter. */
const UnitSizeInput = ({ px, unit, onCommit, inputRef, testId, label }) => {
  const u = SIZE_UNITS.find((x) => x.id === unit) || SIZE_UNITS[0];
  const fmt = (v) => (v * u.perPx).toFixed(u.digits);
  const [text, setText] = useState(fmt(px));
  const [editing, setEditing] = useState(false);
  useEffect(() => { if (!editing) setText((px * u.perPx).toFixed(u.digits)); }, [px, u, editing]);
  const commit = () => {
    setEditing(false);
    const v = parseFloat(String(text).replace(',', '.'));
    if (!Number.isFinite(v)) { setText(fmt(px)); return; }
    const next = Math.round(Math.min(MAX_PAGE_PX, Math.max(MIN_PAGE_PX, v / u.perPx)));
    if (next !== px) onCommit(next); else setText(fmt(px));
  };
  const nudge = (dir) => { const v = (parseFloat(text) || px * u.perPx) + dir * u.step; onCommit(Math.round(Math.min(MAX_PAGE_PX, Math.max(MIN_PAGE_PX, v / u.perPx)))); };
  return (
    <div className="relative mt-1">
      <Input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        value={text}
        aria-label={`${label} (${u.label})`}
        onFocus={() => setEditing(true)}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.currentTarget.blur(); } else if (e.key === 'ArrowUp') { e.preventDefault(); nudge(1); } else if (e.key === 'ArrowDown') { e.preventDefault(); nudge(-1); } }}
        className="bg-background border-border pr-9"
        data-testid={testId}
        data-px={px}
      />
      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] text-muted-foreground pointer-events-none">{u.label}</span>
    </div>
  );
};

const PageProperties = ({ page, pageIndex, dispatch, pxToUnit }) => {
  const { PAGE_PRESETS, openDialog, state } = useEditor();
  const widthRef = React.useRef(null);
  // Size unit: follows the ruler unit (View ▸ Units) and can be switched here; in / cm / pt / px also switch the rulers
  const [sizeUnit, setSizeUnit] = useState(() => (SIZE_UNITS.some((x) => x.id === state.canvasUnit) ? state.canvasUnit : 'in'));
  useEffect(() => { if (SIZE_UNITS.some((x) => x.id === state.canvasUnit)) setSizeUnit(state.canvasUnit); }, [state.canvasUnit]);
  const changeUnit = (u) => { setSizeUnit(u); if (CANVAS_UNITS.includes(u) && u !== state.canvasUnit) dispatch({ type: 'SET_CANVAS_UNIT', payload: u }); };
  const presetKey = presetKeyForPage(page) || 'custom';
  const orientation = orientationOf(page); // 'portrait' | 'landscape' | 'square'
  
  const updatePage = (data) => {
    dispatch({ type: 'UPDATE_PAGE', payload: { index: pageIndex, data } });
  };
  // Portrait / Landscape swaps width and height for every page, exactly like Page Design ▸ Orientation
  const setOrientation = (want) => {
    if (orientation === want || orientation === 'square') return;
    dispatch({ type: 'UPDATE_ALL_PAGES_SIZE', payload: { width: page.height, height: page.width } });
    toast.success(`Orientation: ${want === 'landscape' ? 'Landscape' : 'Portrait'}`);
  };
  
  return (
    <div className="p-4 space-y-4">
      <div>
        <Label className="text-xs text-foreground/70">Page Name</Label>
        <Input
          value={page.name}
          onChange={(e) => updatePage({ name: e.target.value })}
          className="mt-1 bg-background border-border"
          data-testid="page-props-name-input"
        />
      </div>
      
      <div>
        <Label className="text-xs text-foreground/70">Page Size</Label>
        <Select
          value={presetKey}
          onValueChange={(value) => {
            if (value === 'custom') {
              // Custom size: open Page Setup (width / height in the canvas unit, apply to one or all pages)
              openDialog('page-setup', { focus: 'width' });
              return;
            }
            const preset = PAGE_PRESETS[value];
            // the preset's own orientation (Banner → Letter gives a portrait Letter) unless this page was deliberately turned
            const size = sizeForPreset(preset, page);
            dispatch({ type: 'UPDATE_ALL_PAGES_SIZE', payload: size });
          }}
        >
          <SelectTrigger className="mt-1 bg-background border-border text-foreground" data-testid="page-props-size-select">
            <SelectValue placeholder="Select size" />
          </SelectTrigger>
          <SelectContent className="bg-popover border-border text-popover-foreground">
            {Object.entries(PAGE_PRESETS).map(([key, preset]) => (
              <SelectItem key={key} value={key} className="hover:bg-accent focus:bg-accent">
                {preset.name}
              </SelectItem>
            ))}
            <SelectItem value="custom" className="hover:bg-accent focus:bg-accent" data-testid="page-props-size-custom">Custom…</SelectItem>
          </SelectContent>
        </Select>
        <div className="mt-2 flex items-center justify-between gap-2">
          <Label className="text-xs text-foreground/70">Orientation</Label>
          <div className="inline-flex rounded-md border border-border overflow-hidden" role="radiogroup" aria-label="Orientation" data-testid="page-props-orientation">
            {[['portrait', 'Portrait', RectangleVertical], ['landscape', 'Landscape', RectangleHorizontal]].map(([id, label, Icon]) => {
              const on = orientation === id;
              return (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  disabled={orientation === 'square'}
                  title={orientation === 'square' ? 'A square page has no orientation' : label}
                  onClick={() => setOrientation(id)}
                  className={`h-7 px-2.5 inline-flex items-center gap-1.5 text-[11px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed ${on ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
                  data-testid={`page-props-orientation-${id}`}
                >
                  <Icon className="h-3.5 w-3.5" aria-hidden="true" />{label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      
      <div className="flex items-center justify-between">
        <Label className="text-xs text-foreground/70">Page size · units</Label>
        <div className="inline-flex rounded-md border border-border overflow-hidden" role="radiogroup" aria-label="Size units" data-testid="page-props-size-units">
          {SIZE_UNITS.map((u) => (
            <button key={u.id} type="button" role="radio" aria-checked={sizeUnit === u.id} onClick={() => changeUnit(u.id)} className={`h-6 px-2 text-[11px] transition-colors ${sizeUnit === u.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`} data-testid={`page-props-unit-${u.id}`}>{u.label}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-foreground/70">Width</Label>
          <UnitSizeInput px={page.width} unit={sizeUnit} inputRef={widthRef} label="Width" onCommit={(width) => updatePage({ width })} testId="page-props-width-input" />
        </div>
        <div>
          <Label className="text-xs text-foreground/70">Height</Label>
          <UnitSizeInput px={page.height} unit={sizeUnit} label="Height" onCommit={(height) => updatePage({ height })} testId="page-props-height-input" />
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground" data-testid="page-props-size-summary">{(page.width / 96).toFixed(2)} × {(page.height / 96).toFixed(2)} in · {(page.width * 2.54 / 96).toFixed(1)} × {(page.height * 2.54 / 96).toFixed(1)} cm · {page.width} × {page.height} px</p>
      
      <Separator className="bg-border" />
      
      <div>
        <Label className="text-xs text-foreground/70">Background</Label>
        <div className="flex items-center gap-2 mt-1">
          <input
            type="color"
            value={page.background?.type === 'solid' ? page.background?.color || '#ffffff' : '#ffffff'}
            onChange={(e) => updatePage({ background: { type: 'solid', color: e.target.value } })}
            className="w-8 h-8 rounded cursor-pointer"
            aria-label="Background colour"
          />
          <Input
            value={page.background?.type === 'solid' ? page.background?.color || '#ffffff' : page.background?.type === 'gradient' ? 'Gradient' : page.background?.type === 'image' ? 'Picture' : page.background?.type === 'pattern' ? 'Pattern' : '#ffffff'}
            onChange={(e) => updatePage({ background: { type: 'solid', color: e.target.value } })}
            className="flex-1 bg-background border-border"
            aria-label="Background colour (hex)"
            data-testid="page-props-background-input"
          />
        </div>
        <Button type="button" variant="outline" size="sm" className="w-full mt-2 h-8 text-xs border-border" onClick={() => openDialog('fill-effects', { target: 'page', tab: page.background?.type === 'pattern' ? 'pattern' : page.background?.type === 'image' ? 'picture' : 'gradient' })} data-testid="page-props-background-effects">
          <span className="h-3.5 w-3.5 rounded-sm border border-border mr-2 wp-checker overflow-hidden"><span className="block h-full w-full" style={{ background: page.background?.type === 'gradient' ? fillToCss(page.background) : 'linear-gradient(135deg, #7c3aed, #c4b5fd)' }} /></span>
          Gradient, pattern or picture…
        </Button>
      </div>
    </div>
  );
};

const LayersPanel = ({ objects, selectedIds, dispatch, scratchIds = [] }) => {
  const sortedObjects = [...objects].sort((a, b) => b.layer - a.layer);
  const scratchSet = new Set(scratchIds);
  
  const toggleVisibility = (id, visible) => {
    dispatch({ type: 'UPDATE_OBJECT', payload: { id, data: { visible: !visible } } });
  };
  
  const toggleLock = (id, locked) => {
    dispatch({ type: 'UPDATE_OBJECT', payload: { id, data: { locked: !locked } } });
  };
  
  const selectObject = (id, multi) => {
    dispatch({ type: 'SELECT_OBJECT', payload: { id, multi } });
  };
  
  const moveLayer = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < sortedObjects.length) {
      dispatch({ type: 'REORDER_LAYERS', payload: { fromIndex: index, toIndex: newIndex } });
    }
  };
  
  return (
    <ScrollArea className="h-[calc(100vh-160px)]">
      <div className="p-2">
        {sortedObjects.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            <Layers className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No objects on this page</p>
          </div>
        ) : (
          <div className="space-y-1">
            {sortedObjects.map((obj, index) => (
              <div
                key={obj.id}
                className={`layer-item flex items-center gap-2 p-2 rounded cursor-pointer ${
                  selectedIds.includes(obj.id) ? 'selected bg-primary/15 border-l-2 border-primary' : ''
                }`}
                onClick={(e) => selectObject(obj.id, e.shiftKey)}
                data-testid={`layer-item-${obj.id}`}
                data-object-type={obj.type}
              >
                <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab" />
                
                <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                  {obj.type === 'text' && <span className="text-xs font-bold">T</span>}
                  {obj.type === 'textbox' && <span className="text-xs font-bold">Tb</span>}
                  {obj.type === 'shape' && (
                    <div 
                      className="w-4 h-4 rounded-sm"
                      style={{ background: obj.properties?.fill?.color || '#7c3aed' }}
                    />
                  )}
                  {obj.type === 'image' && <Image className="w-4 h-4" />}
                  {obj.type === 'group' && <Layers className="w-4 h-4" />}
                </div>
                
                <span className="flex-1 text-sm truncate text-foreground">
                  {obj.type === 'text' || obj.type === 'textbox' 
                    ? (obj.properties?.text?.substring(0, 20) || 'Text') 
                    : obj.type.charAt(0).toUpperCase() + obj.type.slice(1)}
                </span>
                {scratchSet.has(obj.id) && (
                  <span className="shrink-0 text-[10px] leading-4 px-1 rounded-sm bg-muted text-muted-foreground border border-border" title="Scratch area: beside the page, shown next to every page, never printed" data-testid={`layer-scratch-tag-${obj.id}`}>Scratch</span>
                )}
                
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-6 h-6 text-muted-foreground hover:text-foreground"
                    onClick={(e) => { e.stopPropagation(); moveLayer(index, 'up'); }}
                    disabled={index === 0}
                  >
                    <ChevronUp className="w-3 h-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-6 h-6 text-muted-foreground hover:text-foreground"
                    onClick={(e) => { e.stopPropagation(); moveLayer(index, 'down'); }}
                    disabled={index === sortedObjects.length - 1}
                  >
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-6 h-6 text-muted-foreground hover:text-foreground"
                    onClick={(e) => { e.stopPropagation(); toggleVisibility(obj.id, obj.visible); }}
                  >
                    {obj.visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-6 h-6 text-muted-foreground hover:text-foreground"
                    onClick={(e) => { e.stopPropagation(); toggleLock(obj.id, obj.locked); }}
                  >
                    {obj.locked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </ScrollArea>
  );
};
